/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export interface SummaryRef {
  getHtml: () => string;
  getFullHtml: () => string;
}

interface SummaryProps {
  title?: string;
  content?: string;
  index: number;
  companyLogo?: string;
}

const Terms = forwardRef<SummaryRef, SummaryProps>(
  ({ content = "", index, companyLogo }, ref) => {
    const quillRef = useRef<any>(null);
    const [termsText, setTermsText] = useState<string>(content);

    useImperativeHandle(ref, () => ({
      getHtml: () => {
        const value = quillRef.current?.getEditor()?.root.innerHTML || "";
        return `${value}`;
      },
      getFullHtml: () => {
        const value = quillRef.current?.getEditor()?.root.innerHTML || "";
        return `<div class="summary_section">
        <div class="header">
          <div class="proposal_title main_title">
            <div class="page_counter"> 0${index} </div>
            <h4 style="margin-left: 24px;"> Terms and Conditions </h4>
          </div>
        <div class="logo_container">
          ${
            companyLogo
              ? `<img src="${companyLogo}" class="temp_logo" alt="logo" />`
              : `<img src="/images/company/no_image_grey.png" class="temp_logo" alt="logo"/>`
          }
          </div>
        </div>

        <div class="content">
          ${value}
        </div>
      </div>`;
      },
    }));

    useEffect(() => {
      const coverText = `
      <h4>Proposal Validity</h4>
      <p>This Proposal is based on the plans, specifications, and documents provided to Evergreen Landscapes as of the date hereof.
      All pricing is valid for sixty (60) days from the Proposal date. If a signed Letter of Intent, Contract, or other execution document is not received within sixty (60) days, quoted prices may be adjusted to reflect current costs.</p>
      <p><br/></p>
      <h4>Scope of Work & Mobilization</h4>
      <p>Pricing reflects a single mobilization to the project site unless otherwise noted. Should the work be phased or require multiple mobilizations, Evergreen Landscapes reserves the right to adjust pricing to cover additional mobilization costs.</p>
      <p>An initial mobilization fee (as detailed in the Schedule of Values) will be included in the first invoice or deposit request.</p>
      <p><br/></p>
      <h4>Alternates & Change Orders</h4>
      <p>Items not included in this Proposal’s Base Scope are listed under “Alternates.” Any Alternate selected by the Owner must be confirmed in writing and will be priced separately.</p>
      <p>Unit or line‐item pricing provided herein shall not automatically apply to Field Change Orders unless Evergreen Landscapes explicitly agrees in writing. All changes in scope or design must be documented via a written Change Order signed by both the Owner and Evergreen Landscapes.</p>
      <p><br/></p>
      <h4>Utility Locates</h4>
      <p>Evergreen Landscapes will coordinate and be responsible for locating all public utilities through standard utility‐location services (e.g., 811 or regional “call‐before‐you‐dig” providers).</p>
      <p>Any private or non‐public utility lines that require additional locates beyond normal one‐call services are excluded from this Proposal. Locating, adjusting, or protecting private utilities is the Owner’s responsibility and cost.</p>
      <p><br/></p>
      <h4>Bonding & Permitting</h4>
      <p>Unless specifically indicated otherwise in the Proposal’s cost breakdown, all required performance bonds, payment bonds, permit fees, plan reviews, and related expenses are excluded.</p>
      <p>Should bonding or permitting be needed, Evergreen Landscapes can assist in securing the required documents upon Owner authorization; associated fees will be invoiced as an Extra.</p>
      <p><br/></p>
      <h4>Price Escalation Clause</h4>
      <p>The Contract Price is based on current costs for labor, materials, equipment, energy, and prevailing wages at the time of this Proposal. Market conditions for these inputs can fluctuate rapidly.</p>
      <p>Evergreen Landscapes commits to diligently pursue the lowest available costs. However, if, at the time of procurement or installation, the actual cost of any key material, equipment component, or labor category increases beyond the amount built into this Proposal, such difference shall be added to the Contract Price by Change Order.</p>
      <p><br/></p>
      <h4>Exclusions</h4>
      <p>Unless specifically included in the Base Scope or an approved Alternate, the following are excluded:</p>
      <ul>
        <li><strong>Demolition & Site Preparation:</strong> Removal of existing structures, hardscape, or debris.</li>
        <li><strong>Topsoil & Rough Grading:</strong> Import/export of topsoil, subgrade adjustment, or re-compaction beyond light tilling/raking for sod and seed beds.</li>
        <li><strong>Landscape Site Clearing & Tree Work:</strong> Tree removal, stump grinding, or major pruning unless explicitly listed.</li>
        <li><strong>Owner‐Provided Materials:</strong> Any specialty stone, decorative gravel, or plant materials designated as Owner‐furnished.</li>
        <li><strong>Other Exclusions:</strong> Hauling/disposal of contaminated soil or hazardous materials; any work not described in the Scope of Work or approved Alternate.</li>
      </ul>
      <p><br/></p>
      <h4>Planting Guarantee</h4>
      <p>Evergreen Landscapes warrants all living plant materials installed under this Proposal (excluding turf) for one (1) year from installation, provided a maintenance agreement is maintained.</p>
      <p>This warranty excludes damage from:</p>
      <ul>
        <li>Vandalism, theft, or animal damage</li>
        <li>Acts of God (e.g., flood, fire, freeze)</li>
        <li>Neglect by Owner or third-parties (e.g., no watering)</li>
        <li>Mechanical/equipment damage not by Evergreen</li>
      </ul>
      <p>Written notice of loss must be received within 30 days of observation.</p>
      <p><br/></p>
      <h4>Governing Law & Dispute Resolution</h4>
      <p>This Proposal and any resulting Contract shall be governed by the laws of the project state. Disputes will first be negotiated by senior management, then resolved via binding arbitration if unresolved within 30 days.</p>
  `;
      setTermsText(coverText);
    }, []);

    return (
      <div className="summary_section">
        <div className="header">
          <div className="proposal_title">
            <div className="page_counter"> 0{index} </div>
            <h4 style={{ marginLeft: "24px" }}> Terms and Conditions </h4>
          </div>
          <div className="logo_container">
            {companyLogo ? (
              <img
                src={companyLogo}
                className="temp_logo"
                alt="template logo"
              />
            ) : (
              <img
                src="/images/company/no_image_grey.png"
                className="temp_logo"
                alt="logo"
              />
            )}
          </div>
        </div>

        <div className="content quill_container_editor">
          <ReactQuill
            modules={{
              toolbar: [
                [{ header: [1, 2, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["link", "image"],
                ["clean"],
              ],
            }}
            formats={[
              "header",
              "bold",
              "italic",
              "underline",
              "strike",
              "list",
              "bullet",
              "align",
              "link",
              "image",
            ]}
            ref={quillRef}
            value={termsText}
            onChange={setTermsText}
          />
        </div>
      </div>
    );
  }
);

export default Terms;
